import { useState, useRef, useEffect, useMemo } from 'react';
import { HiTag, HiChevronDown, HiSearch, HiCheck, HiX } from 'react-icons/hi';

export default function CategoryMultiSelect({ categories, selected, onChange, loading }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? categories.filter((c) => c.name.toLowerCase().includes(q)) : categories;
  }, [categories, query]);

  const toggle = (id) =>
    onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]);

  const label =
    selected.length === 0
      ? 'كل التصنيفات'
      : selected.length === 1
        ? categories.find((c) => c.id === selected[0])?.name || 'تصنيف واحد'
        : `${selected.length} تصنيفات`;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={loading}
        className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border bg-gray-50/50 text-sm text-right transition-all focus:outline-none focus:ring-2 focus:ring-primary-500/20 disabled:opacity-60 ${
          open ? 'border-primary-500 ring-2 ring-primary-500/20' : 'border-gray-200 hover:border-primary-200'
        }`}
      >
        <HiTag className="w-4 h-4 text-primary-300 flex-shrink-0" />
        <span className={`flex-1 truncate ${selected.length ? 'font-semibold text-gray-800' : 'text-gray-500'}`}>
          {loading ? 'جاري التحميل...' : label}
        </span>
        {selected.length > 0 && (
          <span className="text-[11px] font-bold text-white bg-primary-500 rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">
            {selected.length}
          </span>
        )}
        <HiChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-30 mt-2 w-full min-w-[260px] bg-white rounded-2xl shadow-xl border border-primary-100/60 overflow-hidden animate-slideDown">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <HiSearch className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="ابحث عن تصنيف..."
                className="w-full pr-9 pl-3 py-2 rounded-lg bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              />
            </div>
          </div>

          <div className="flex items-center justify-between px-3 py-2 text-xs border-b border-gray-50">
            <button
              type="button"
              onClick={() => onChange(categories.map((c) => c.id))}
              className="font-semibold text-primary-600 hover:text-primary-700"
            >
              تحديد الكل
            </button>
            <button
              type="button"
              onClick={() => onChange([])}
              disabled={selected.length === 0}
              className="font-semibold text-gray-400 hover:text-red-500 disabled:opacity-40 disabled:hover:text-gray-400"
            >
              مسح التحديد
            </button>
          </div>

          <ul role="listbox" aria-multiselectable="true" className="max-h-64 overflow-y-auto py-1">
            {visible.length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-gray-400">لا توجد نتائج</li>
            )}
            {visible.map((c) => {
              const checked = selected.includes(c.id);
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={checked}
                    onClick={() => toggle(c.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-right transition-colors ${
                      checked ? 'bg-primary-50/70' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                        checked ? 'bg-primary-500 border-primary-500' : 'border-gray-300'
                      }`}
                    >
                      {checked && <HiCheck className="w-3.5 h-3.5 text-white" />}
                    </span>
                    <span className={`flex-1 truncate ${checked ? 'font-semibold text-primary-700' : 'text-gray-700'}`}>
                      {c.name}
                    </span>
                    {c._count?.products != null && (
                      <span className="text-[11px] text-gray-400">{c._count.products} منتج</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export function SelectedCategoryChips({ categories, selected, onChange }) {
  if (selected.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {selected.map((id) => {
        const cat = categories.find((c) => c.id === id);
        if (!cat) return null;
        return (
          <span
            key={id}
            className="inline-flex items-center gap-1 pr-3 pl-1.5 py-1 rounded-full bg-primary-50 border border-primary-100 text-xs font-semibold text-primary-700 animate-popIn"
          >
            {cat.name}
            <button
              type="button"
              onClick={() => onChange(selected.filter((s) => s !== id))}
              aria-label={`إزالة ${cat.name}`}
              className="p-0.5 rounded-full hover:bg-primary-100 text-primary-400 hover:text-primary-700"
            >
              <HiX className="w-3 h-3" />
            </button>
          </span>
        );
      })}
    </div>
  );
}
